import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import { invalid } from '../validation.ts';
import {
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

interface WindowWithChrome extends Window {
  readonly chrome?: Record<string, unknown>;
}

async function collect(): Promise<RawData> {
  const windowChrome = (window as WindowWithChrome).chrome;
  const plugins = [...navigator.plugins].map((plugin) => ({
    name: plugin.name,
    filename: plugin.filename,
    description: plugin.description,
    mimeTypes: [...plugin].map((mime) => mime.type).sort(),
  }));
  const mimeTypes = [...navigator.mimeTypes].map((mime) => ({
    type: mime.type,
    suffixes: mime.suffixes,
    description: mime.description,
  }));
  return {
    windowChromeKeys: windowChrome ? Object.keys(windowChrome).sort() : [],
    pdfViewerEnabled: navigator.pdfViewerEnabled,
    plugins: plugins.sort((left, right) => left.name.localeCompare(right.name)),
    mimeTypes: mimeTypes.sort((left, right) =>
      left.type.localeCompare(right.type),
    ),
  };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return `${rawData.pdfViewerEnabled ? 'PDF enabled' : 'PDF disabled'} · ${Array.isArray(rawData.plugins) ? rawData.plugins.length : 0} plugins`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return rawData.pdfViewerEnabled === true && Array.isArray(rawData.plugins)
    ? validation([])
    : invalid('Google Chrome PDF/plugin capabilities are incomplete');
}

export const chromeCapabilitiesTest: TestDefinition = {
  id: 'chromeCapabilities',
  title: 'Chrome capabilities',
  category: 'shared',
  format,
  run,
  validate,
};
